import { bookActions } from '@/actions/bookActions';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { AppDispatch } from '@/store';
import { BookState, ExtraTimeRequirement } from '@/types/state/book';
import { PlaceEmployee, PlaceService } from '@/types/state/shared';
import { User } from '@/types/user';
import { getPickedEmployee, hasMultipleEmployees } from './BookAppointment.helpers';
import { BookAppointmentContext, Config } from './BookAppointment.hooks';
import { View } from './BookAppointment.types';
import { ANY_AMPLOYEE } from './BookAppointments.constants';

export function handleOnPickService(
  context: Pick<BookAppointmentContext, 'view' | 'close' | 'updateView' | 'updateUrlState'>,
  bookState: BookState,
  dispatch: AppDispatch,
  config?: Config,
) {
  const { place, services: pickedServices, employee } = bookState;

  const pickedEmployee = getPickedEmployee(employee, place.employees);
  const pickedRandomEmployee = employee === ANY_AMPLOYEE.id;

  return (service: PlaceService, isRecommended: boolean) => {
    const [pickedAddonServices, pickedMainServices] = [
      (pickedServices ?? []).filter((service) => service?.about?.settings?.isAddOn),
      (pickedServices ?? []).filter((service) => !service?.about?.settings?.isAddOn),
    ];
    if (isRecommended) {
      dispatch(bookActions.appendExtraService(service, pickedServices));
    } else {
      // @ts-ignore
      dispatch(bookActions.appendService(service, place, undefined, pickedServices, employee, false));
    }

    /**
     * handle view updates if not disabled in config
     */
    if (!config?.disableViewUpdate) {
      /**
       * handle change view if not picking a recommended service
       */
      if (!isRecommended) {
        if (pickedEmployee || pickedRandomEmployee) {
          context.updateView('summary');
        } else {
          context.updateView('pickEmployee');
        }
      }
    }

    /**
     * handle URL updates if not disabled in config
     */
    if (!config?.disableUrlUpdate) {
      const type = isRecommended ? 'addOnServices' : 'mainServices';
      const serviceIds = isRecommended
        ? [service.id, ...pickedAddonServices.map((s) => s.id)]
        : [service.id, ...pickedMainServices.map((s) => s.id)];

      context.updateUrlState({ type, serviceIds });
    }

    if (!config?.disableTracking) {
      const service_ids = [
        service.id,
        ...pickedMainServices.map((s) => s.id),
        ...pickedAddonServices.map((s) => s.id),
      ].map((id) => id.toString());

      bookingFlowTrackingSession.setProperties({ service_ids });

      if (isRecommended) {
        bookingFlowTrackingSession.addRecommendedService();
      } else {
        if (bookingFlowTrackingSession.getStepCategory() === 'add_service') {
          bookingFlowTrackingSession.addService('success');
        } else {
          bookingFlowTrackingSession.serviceSelected();
        }
      }
    }
  };
}

export function handleOnPickEmployee(
  context: Pick<BookAppointmentContext, 'updateView' | 'updateUrlState'>,
  dispatch: AppDispatch,
  config?: Config,
) {
  return (employee: PlaceEmployee) => {
    if (!employee) {
      dispatch(bookActions.pickEmployee(ANY_AMPLOYEE.id, undefined));
    } else {
      dispatch(bookActions.pickEmployee(employee.id, employee.about.priceListId || 0));
    }

    if (!config?.disableViewUpdate) {
      context.updateView('summary');
    }

    if (!config?.disableUrlUpdate) {
      context.updateUrlState?.({ type: 'employee', externalId: employee?.about?.externalId ?? undefined });
    }

    if (!config?.disableTracking) {
      bookingFlowTrackingSession.employeeSelected(employee?.id ? 'specific' : 'any');
    }
  };
}

export function handleOnRemoveService(
  context: Pick<BookAppointmentContext, 'view' | 'close' | 'updateView' | 'updateUrlState'>,
  bookstate: BookState,
  dispatch: AppDispatch,
  config?: Config,
) {
  const { place, services: pickedServices, employee, time } = bookstate;
  const [pickedAddonServices, pickedMainServices] = [
    (pickedServices || []).filter((service) => service?.about?.settings?.isAddOn),
    (pickedServices || []).filter((service) => !service?.about?.settings?.isAddOn),
  ];

  return (serviceId: number) => {
    // @ts-ignore
    dispatch(bookActions.popService(serviceId, time?.timestamp, pickedServices, employee, place, true));

    const isRecommended = pickedServices.some((service) => service.about.settings.isAddOn);

    if (!config?.disableUrlUpdate) {
      const type = isRecommended ? 'addOnServices' : 'mainServices';
      const serviceIds = isRecommended
        ? pickedAddonServices.filter((s) => s.id !== serviceId).map((s) => s.id)
        : pickedMainServices.filter((s) => s.id !== serviceId).map((s) => s.id);

      context.updateUrlState?.({ type, serviceIds: serviceIds.length ? serviceIds : undefined });
    }

    if (!config?.disableTracking) {
      const service_ids = pickedServices.filter((service) => service.id !== serviceId).map((s) => s.id.toString());
      bookingFlowTrackingSession.setProperties({ service_ids });
      bookingFlowTrackingSession.removedService();
    }
  };
}

export function handleGoBack(
  context: Pick<BookAppointmentContext, 'view' | 'close' | 'updateView' | 'updateUrlState'>,
  bookstate: BookState,
  dispatch: AppDispatch,
  config?: Config,
) {
  const { place, employee } = bookstate;
  const showPickedEmployee = hasMultipleEmployees(place) && !Boolean(place.about?.settings?.hideEmployees);
  const pickedEmployee: PlaceEmployee | undefined = (() => {
    if (place.about?.settings?.hideEmployees) {
      return undefined;
    }

    const picked = getPickedEmployee(employee, place.employees);
    return picked ?? ANY_AMPLOYEE;
  })();

  return (from: View) => {
    switch (from) {
      case 'summary': {
        let previousView = context.view.previous;

        /**
         * never go back to pickServices on back action from summary
         */
        if (previousView === 'pickServices') {
          previousView = undefined;
        }

        if (previousView === 'pickExtraTime') {
          previousView = undefined;
        }

        if (pickedEmployee) {
          const view = previousView ? previousView : showPickedEmployee ? 'pickEmployee' : undefined;

          if (!view) {
            context.close();
            return;
          }

          if (view === 'pickEmployee') {
            /**
             * If going back to pickEmployee view, remove picked employee from state and url
             */
            if (pickedEmployee) {
              dispatch(bookActions.removeEmployee());
              context.updateUrlState?.({ type: 'employee', externalId: undefined });
            }
          }

          /**
           * Tracking back events
           */
          if (!config?.disableTracking) {
            if (view === 'pickEmployee') {
              bookingFlowTrackingSession.backToEmployeeSelection();
            }

            if (view === 'pickService') {
              bookingFlowTrackingSession.backToServiceSelection();
            }
          }

          context.updateView?.(view);
          return;
        }

        previousView ? context.updateView?.(previousView) : context.close();
        return;
      }
      case 'pickServices': {
        let previousView = context.view.previous;

        if (!previousView) {
          context.close();
          return;
        }

        if (!config?.disableTracking) {
          bookingFlowTrackingSession.addService('cancel');
        }

        context.updateView(previousView);
        return;
      }
      case 'pickExtraTime': {
        let previousView = context.view.previous;
        if (!previousView) {
          context.close();
          return;
        }

        context.updateView(previousView);
      }
      default:
        break;
    }
  };
}

export function handleOnPickExtraTime(
  context: Pick<BookAppointmentContext, 'view' | 'close' | 'updateView' | 'updateUrlState'>,
  bookstate: BookState,
  dispatch: AppDispatch,
  config?: Config,
  user?: User,
) {
  return (extraTime: ExtraTimeRequirement) => {
    const { place } = bookstate;
    // @ts-ignore
    dispatch(bookActions.setExtraTimeRequirement(extraTime, place?.id, user));
  };
}
